import type { TFunction } from "i18next";
import { AnimatePresence, motion } from "motion/react";
import { useMemo } from "react";
import { Pie, PieChart } from "recharts";

import type { Slice } from "../hooks/useDistributionSlices";

import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/v2/chart";

const NAME_KEY = "activeCampaignDistributionChart";
const DIMMED_OPACITY = 0.35;

interface Props {
  rows: Slice[];
  chartConfig: ChartConfig;
  totalCampaignCount: number;
  hoveredKey: string | null;
  onHover: (key: string | null) => void;
  numberFormatter: Intl.NumberFormat;
  percentFormatter: Intl.NumberFormat;
  t: TFunction;
}

export function DistributionPie({
  rows,
  chartConfig,
  totalCampaignCount,
  hoveredKey,
  onHover,
  numberFormatter,
  percentFormatter,
  t,
}: Props) {
  const pieData = useMemo(
    () =>
      rows.map((r) => ({
        ...r,
        fillOpacity:
          hoveredKey === null || hoveredKey === r.key ? 1 : DIMMED_OPACITY,
      })),
    [rows, hoveredKey],
  );

  const hovered = hoveredKey ? rows.find((r) => r.key === hoveredKey) : null;
  const transition = { duration: 0.18, ease: [0.22, 1, 0.36, 1] as const };

  return (
    <div
      className="relative aspect-square w-[80%] min-w-[200px] max-w-[360px] shrink-0"
      role="img"
      aria-label={t("home.activeCampaignDistribution.totalCampaignsAria", {
        total: numberFormatter.format(totalCampaignCount),
      })}
    >
      <ChartContainer
        config={chartConfig}
        className="aspect-square h-full w-full [&_.recharts-sector]:transition-[fill-opacity] [&_.recharts-sector]:duration-200 [&_.recharts-sector]:ease-out"
      >
        <PieChart>
          <ChartTooltip
            cursor={false}
            content={
              <ChartTooltipContent
                hideLabel
                nameKey={NAME_KEY}
                formatter={(_value, _name, item) => {
                  const slice = item?.payload as Slice | undefined;
                  if (!slice) return null;
                  const campaignLabel = t(
                    "home.activeCampaignDistribution.tooltip.activeCampaigns",
                    { count: slice.value },
                  );
                  const label = chartConfig[slice.key]?.label ?? slice.label;
                  return (
                    <>
                      <span
                        aria-hidden
                        className="size-2 shrink-0 self-center rounded-full"
                        style={{ backgroundColor: slice.fill }}
                      />
                      <span className="self-center">{label}</span>
                      <span className="ml-auto self-center tabular-nums">
                        <span className="font-medium">{campaignLabel}</span>
                      </span>
                    </>
                  );
                }}
              />
            }
          />
          <Pie
            data={pieData}
            dataKey="value"
            nameKey={NAME_KEY}
            innerRadius="80%"
            outerRadius="100%"
            paddingAngle={0}
            strokeWidth={2}
            isAnimationActive={false}
            onMouseEnter={(_, index) => {
              const slice = pieData[index];
              if (slice && !slice.isOther) onHover(slice.key);
            }}
            onMouseLeave={() => onHover(null)}
          />
        </PieChart>
      </ChartContainer>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-6 text-center"
      >
        <AnimatePresence mode="wait" initial={false}>
          {hovered ? (
            <motion.div
              key={hovered.key}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={transition}
              className="flex flex-col items-center"
            >
              <span className="text-3xl font-bold tabular-nums">
                {numberFormatter.format(hovered.value)}
                {totalCampaignCount > 0 && (
                  <span className="text-muted-foreground ml-1 text-lg font-medium">
                    ({percentFormatter.format(
                      Math.min(hovered.value / totalCampaignCount, 1),
                    )})
                  </span>
                )}
              </span>
              <span className="text-muted-foreground mt-1 max-w-full truncate text-sm">
                {hovered.label}
              </span>
            </motion.div>
          ) : (
            <motion.div
              key="__total__"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={transition}
              className="flex flex-col items-center"
            >
              <span className="text-3xl font-bold tabular-nums">
                {numberFormatter.format(totalCampaignCount)}
              </span>
              <span className="text-sm text-muted-foreground">
                {t("home.activeCampaignDistribution.totalCampaigns")}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
